import joborderlpoService from '../../../services/joborderlpoService';
import filterService from '../../../services/filterService';

import {
    setJobOrderLpoLoading,
    setJobOrderLpoError,
    setJobOrderLpoList,
    setJobOrderLpoCurrent,
    setJobOrderLpoPagination,
    setJobOrderLpoSort,
    setJobOrderLpoFilters,
    setJobOrderLpoDefaultFilter,
    setJobOrderLpoSettings,
    clearJobOrderLpoCurrent,
    clearJobOrderLpoError,
    setJobOrderLpoFilterLoading,
    changeJobOrderLpoFilterCondition,
    addJobOrderLpoFilterCondition,
    updateJobOrderLpoFilterConditionRule,
    updateJobOrderLpoFilterConditionValue,
    removeJobOrderLpoFilterCondition,
    setJobOrderLpoSelected,
    resetJobOrderLpoFiltersAndPagination
} from './joborderlpoSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().joborderlpo.isSettingsInitialized) {
            dispatch(setJobOrderLpoLoading(true));
            const response = await joborderlpoService.getSettings();

            // Set the settings
            dispatch(setJobOrderLpoSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setJobOrderLpoDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setJobOrderLpoError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setJobOrderLpoLoading(false));
    }
};

export const fetchJoborderlpos = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().joborderlpo;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setJobOrderLpoLoading(true));
        const response = await joborderlpoService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setJobOrderLpoList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchJoborderlpos:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setJobOrderLpoError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobOrderLpoLoading(false));
    }
};

export const fetchJobOrderLpo = (id) => async (dispatch) => {
    try {
        dispatch(setJobOrderLpoLoading(true));
        const response = await joborderlpoService.getOne(id);
        dispatch(setJobOrderLpoCurrent(response.data));
    } catch (error) {
        dispatch(setJobOrderLpoError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setJobOrderLpoLoading(false));
    }
};

export const createJobOrderLpo = (data) => async (dispatch) => {
    try {
        // const response = await joborderlpoService.createWithFile(data);
        const response = await joborderlpoService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateJobOrderLpo = (id, data) => async (dispatch) => {
    try {
        const response = await joborderlpoService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteJobOrderLpo = (id) => async (dispatch) => {
    try {
        dispatch(setJobOrderLpoLoading(true));
        const response = await joborderlpoService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setJobOrderLpoError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobOrderLpoLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setJobOrderLpoPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setJobOrderLpoSort(sort));
};

export const searchJoborderlpos = (query) => (dispatch, getState) => {
    const currentFilters = getState().joborderlpo.filters;
    dispatch(setJobOrderLpoFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setJobOrderLpoDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeJobOrderLpoFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addJobOrderLpoFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateJobOrderLpoFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateJobOrderLpoFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeJobOrderLpoFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setJobOrderLpoFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().joborderlpo.settings;
            dispatch(setJobOrderLpoSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setJobOrderLpoFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setJobOrderLpoFilterLoading(false));
        dispatch(setJobOrderLpoError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setJobOrderLpoSelected(selectedRows));
    };
};

export const clearCurrentJobOrderLpo = () => {
    return (dispatch) => {
        dispatch(clearJobOrderLpoCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetJobOrderLpoFiltersAndPagination());
};